import React, { useCallback, useEffect, useState } from 'react';

import cn from 'classnames';
import { last } from 'lodash';
import { bool, string } from 'prop-types';

import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_ENGLISH_GOOGLE_VALUE,
  DEFAULT_LANGUAGE_ENGLISH,
  EL_SCRIPT_SRC,
  GOOGLE_TRANSLATE_COOKIE_KEY,
  GOOGLE_TRANSLATE_COOKIE_OPTIONS,
  GOOGLE_TRANSLATE_COOKIE_DOMAINS,
  INITIAL_LANGUAGE,
  LANGUAGE_OPTIONS,
  SCRIPT_SELECTORS,
  SCRIPT_SRC,
  SUPPORTED_HUMAN_LANGUAGES,
  EL_LANGUAGE_OPTIONS,
} from 'components/Common/GoogleTranslateV2/constants';

import Select from 'components/Common/Select';
import { debounceIntervalLong } from 'constants/debounceInterval';
import useUserRedux from 'hooks/useUserRedux';
import { isNonEmptyArray } from 'utils/arrays';
import { loadScript } from 'utils/dom';

import s from './GoogleTranslate.css';

import './GoogleTranslateOverrides.css';

function initGoogleTranslate(isElementary) {
  const layoutCode = 0;
  const paramsBase = {
    pageLanguage: DEFAULT_LANGUAGE_ENGLISH,
    layout: layoutCode,
    autoDisplay: false,
    gaTrack: true,
    gaId: 'UA-43742130-1',
  };

  const allLanguages =
    'sq,am,ar,hy,bn,bs,bg,zh-CN,zh-TW,nl,en,tl,fr,de,el,gu,ht,iw,hi,hmn,ig,ilo,it,ja,km,ko,lo,ml,ne,ps,fa,pl,pt,pt-PT,pa,ro,ru,sr,sd,so,es,sw,ta,te,th,tr,uk,ur,vi,yo,rw,my,mn';
  const allParams = {
    ...paramsBase,
    includedLanguages: isElementary ? 'en,es' : allLanguages,
  };
  // eslint-disable-next-line no-new
  new window.google.translate.TranslateElement(
    allParams,
    'google_translate_element',
  );
}

function initGoogleTranslateElementary() {
  initGoogleTranslate(true);
}

window.initGoogleTranslateElementary = initGoogleTranslateElementary;
window.initGoogleTranslate = initGoogleTranslate;

/**
 * @param {string} className Class of the translate button
 * @param {boolean<false>} hidden If true, we hide the translate button from it's placement
 * @param {boolean<false>} isElementary If true, we load scripts correctly for elementary users
 * @param {boolean<false>} isOnboarding If true, we import different style set for the google translate button
 * @returns @GoogleTranslate
 */
const GoogleTranslate = ({ className, hidden, isElementary, isOnboarding }) => {
  const [cookies, setCookie, removeCookie] = useCookies([
    GOOGLE_TRANSLATE_COOKIE_KEY,
  ]);
  const selectedGoogleLanguage = cookies[GOOGLE_TRANSLATE_COOKIE_KEY];
  const { i18n } = useTranslation();
  const { isStudent, isCounselor, isGuardian, isOAuth2Authenticated } =
    useUserRedux();
  const getInitialLanguage = () => {
    if (selectedGoogleLanguage) {
      const googleLanguageKey = last(selectedGoogleLanguage.split('/'));
      return LANGUAGE_OPTIONS.find(
        option => option.value === googleLanguageKey,
      );
    }
    return INITIAL_LANGUAGE;
  };
  const [selectedLanguage, setSelectedLanguage] =
    useState(getInitialLanguage());

  const scriptSrc = isElementary ? EL_SCRIPT_SRC : SCRIPT_SRC;

  const reApplyScript = useCallback(() => {
    // Select the element that's embedded into DOM via script
    const scriptEl = document.querySelector(`script[src="${scriptSrc}"]`);

    // If element already exist, we remove the previously embedded element, to be able to create a new one
    if (scriptEl) {
      scriptEl.parentNode.removeChild(scriptEl);
    }

    // Iterate through script element selectors and remove previously embedded ones into DOM
    SCRIPT_SELECTORS.forEach(selector => {
      const elementToRemove = Array.prototype.slice.call(
        document.querySelectorAll(selector),
      );

      if (isNonEmptyArray(elementToRemove)) {
        elementToRemove.forEach(element => {
          element.parentNode.removeChild(element);
        });
      }
    });

    // Embed new element google translate element into DOM via the script
    loadScript(scriptSrc);
  }, [scriptSrc]);

  // we import correct css stylesheet depending on user type or if user is being onboarded
  useEffect(() => {
    if (isOnboarding) {
      import('components/Common/googleTranslateStyles/onboardingStyles.css');
    } else if (isStudent || isCounselor || isGuardian) {
      import(
        'components/Common/googleTranslateStyles/sideAndTopBarTranslateStyles.css'
      );
    } else if (isOAuth2Authenticated) {
      import(
        'components/Common/googleTranslateStyles/topBarTranslateStyles.css'
      );
    } else {
      import(
        'components/Common/googleTranslateStyles/defaultGoogleTranslateStyles.css'
      );
    }
  }, [isStudent, isCounselor, isGuardian, isOAuth2Authenticated, isOnboarding]);

  useEffect(() => {
    // Set a debounced loader by 1s, to be able to re-apply script to refresh translated items if they haven't.
    const debouncedLoader = setTimeout(() => {
      reApplyScript();
    }, debounceIntervalLong);

    // Clear the timeout when we close the google translator
    return () => clearTimeout(debouncedLoader);
  }, [hidden, reApplyScript, selectedGoogleLanguage]);

  // Updated handleSetLanguage function
  const handleSetLanguage = val => {
    // Remove all existing googtrans cookies
    GOOGLE_TRANSLATE_COOKIE_DOMAINS.forEach(domain => {
      // Remove with standard options
      removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
        ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
        domain,
      });

      // Also try without expiration
      removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
        path: '/',
        domain,
      });
    });

    // Also try removing without domain specification
    removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
      ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
    });

    // Reset selected language on clear
    if (!val) {
      i18n.changeLanguage(DEFAULT_LANGUAGE_ENGLISH);
      setSelectedLanguage(INITIAL_LANGUAGE);
      if (cookies[GOOGLE_TRANSLATE_COOKIE_KEY]) {
        setCookie(GOOGLE_TRANSLATE_COOKIE_KEY, DEFAULT_ENGLISH_GOOGLE_VALUE, {
          ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
          encode: v => v,
        });
        window.location.reload();
      }
      return;
    }

    // Use i18n for languages that we support
    if (val && SUPPORTED_HUMAN_LANGUAGES.includes(val.value)) {
      setSelectedLanguage(val);
      i18n.changeLanguage(val.value);
      setCookie(GOOGLE_TRANSLATE_COOKIE_KEY, `/en/${val.value}`, {
        ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
        encode: v => v,
      });
      return;
    }

    // For unsupported languages, set default language back to English and use Google Translate
    i18n.changeLanguage(DEFAULT_LANGUAGE_ENGLISH);
    setCookie(GOOGLE_TRANSLATE_COOKIE_KEY, `/en/${val.value}`, {
      ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
      encode: v => v,
    });
    setSelectedLanguage(LANGUAGE_OPTIONS.find(opt => opt.value === val.value));
    window.location.reload();
  };
  const showDropdown = !hidden || selectedGoogleLanguage;

  return (
    <>
      <div id="google_translate_element" className={cn(className, s.root)} />
      {showDropdown && (
        <Select
          classes={{
            inputRoot: s.selectInput,
            container: s.selectContainer,
            input: s.input,
          }}
          options={isElementary ? EL_LANGUAGE_OPTIONS : LANGUAGE_OPTIONS}
          onChange={handleSetLanguage}
          renderOption={option => <div>{option?.label}</div>}
          getOptionLabel={option => option?.label}
          getOptionSelected={(option, value) => option?.value === value?.value}
          variant="dropdown"
          width={200}
          value={selectedLanguage}
          noOptionsText="No matching languages"
          readOnlyInput
        />
      )}
    </>
  );
};

GoogleTranslate.propTypes = {
  className: string,
  hidden: bool,
  isElementary: bool,
  isOnboarding: bool,
};

GoogleTranslate.defaultProps = {
  className: undefined,
  hidden: false,
  isElementary: false,
  isOnboarding: false,
};

export default GoogleTranslate;
